// Legacy name keys stay intact; each new Add receives an independent immutable key.
export const extraKey = name => String(name || '').replace(/\s+/g, ' ').trim().toUpperCase();
const newUid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
};
const list = value => { try { const parsed = JSON.parse(value || '[]'); return Array.isArray(parsed) ? parsed : []; } catch (_) { return []; } };
export const readExtraDetails = (body, key, records = []) => {
  const stableKey = extraKey(key);
  const record = records.find(r => r.type === 'EXTRA' && extraKey(r.name) === stableKey) || {};
  for (const node of body.querySelectorAll('.sceneHeadings')) {
    const saved = list(node.getAttribute('data-extra-details')).find(d => d && d.key === stableKey);
    if (saved) return { ...saved, key: stableKey };
  }
  return { key: stableKey, name: record.name || stableKey, quantity: 1, description: record.description || '' };
};
export const writeExtraDetails = (body, key, draft, records = []) => {
  const stableKey = extraKey(key); const name = extraKey(draft.name); const quantity = Number(draft.quantity);
  if (!name) throw new Error('Enter an Extra name.');
  if (!Number.isSafeInteger(quantity) || quantity < 0 || draft.quantity === '') throw new Error('Enter a whole number of Extras, zero or more.');
  const headings = [...body.querySelectorAll('.sceneHeadings')];
  const keys = new Set(headings.flatMap(n => list(n.getAttribute('data-extras-character')).filter(v => typeof v === 'string').map(extraKey)));
  extraDirectory(body, records).forEach(d => keys.add(d.key));
  if (!keys.has(stableKey)) throw new Error('This Extra no longer exists. Reopen Extras.');
  // Display-name equality is not identity. Never merge or borrow another group's scenes.
  const current = readExtraDetails(body, stableKey, records);
  const saved = { ...current, key: stableKey, name, quantity, description: String(draft.description || '') };
  let retained = false;
  for (const node of headings) {
    if (!list(node.getAttribute('data-extras-character')).some(n => extraKey(n) === stableKey) && !list(node.getAttribute('data-extra-details')).some(d => d?.key === stableKey)) continue;
    const details = list(node.getAttribute('data-extra-details')).filter(d => d?.key !== stableKey);
    node.setAttribute('data-extra-details', JSON.stringify([...details, saved]));
    retained = true;
  }
  // Legacy record-only Extras need one retained metadata row even with no scene memberships.
  if (!retained && headings[0]) {
    const details = list(headings[0].getAttribute('data-extra-details'));
    headings[0].setAttribute('data-extra-details', JSON.stringify([...details, saved]));
  }
  if (!retained && !headings.length) throw new Error('Add a scene before saving Extra details.');
  return saved;
};

// Scene references are permanent UIDs when available, with positional fallback for legacy scripts.
export const extraScenes = body => [...body.querySelectorAll('.sceneHeadings')].map((node, index) => ({
  key: node.getAttribute('data-scene-uid') || `index:${index}`,
  number: Number(node.getAttribute('data-scene-index')) || index + 1,
  heading: node.textContent.trim(), node,
})).sort((a, b) => a.number - b.number);
export const extraDirectory = (body, records = []) => {
  const keys = new Set(records.filter(r => r.type === 'EXTRA').map(r => extraKey(r.name)));
  for (const node of body.querySelectorAll('.sceneHeadings')) {
    list(node.getAttribute('data-extras-character')).filter(n => typeof n === 'string').forEach(n => keys.add(extraKey(n)));
    list(node.getAttribute('data-extra-details')).filter(d => d?.key).forEach(d => keys.add(extraKey(d.key)));
  }
  return [...keys].filter(Boolean).map(key => readExtraDetails(body, key, records));
};
export const assignedExtraScenes = (body, key) => extraScenes(body).filter(s => list(s.node.getAttribute('data-extras-character')).some(n => extraKey(n) === extraKey(key)));
export const assignExtraScenes = (body, key, sceneKeys, details) => {
  const stableKey = extraKey(key);
  if (!stableKey) throw new Error('Enter an Extra name.');
  const scenes = extraScenes(body); const wanted = new Set(sceneKeys);
  if ([...wanted].some(k => !scenes.some(s => s.key === k))) throw new Error('A selected scene no longer exists. Reopen Extras.');
  const saved = details || readExtraDetails(body, stableKey);
  for (const { node, key: sceneKey } of scenes) {
    const previous = list(node.getAttribute('data-extras-character'));
    const linked = previous.some(n => extraKey(n) === stableKey);
    if (wanted.has(sceneKey) || linked) {
      const next = previous.filter(n => extraKey(n) !== stableKey);
      if (wanted.has(sceneKey)) next.push(stableKey);
      node.setAttribute('data-extras-character', JSON.stringify(next));
      // Retain details on unlink, including the last scene, so identity/casting is never lost.
      const metadata = list(node.getAttribute('data-extra-details')).filter(d => d?.key !== stableKey);
      node.setAttribute('data-extra-details', JSON.stringify([...metadata, { ...saved, key: stableKey }]));
    }
  }
};
export const addExtraToScene = (body, name, sceneIndex, records = []) => {
  const entered = extraKey(name);
  if (!entered) throw new Error('Enter an Extra name.');
  const scene = [...body.querySelectorAll('.sceneHeadings')][sceneIndex];
  if (!scene) throw new Error('Select a scene before adding an Extra.');
  const keys = new Set(extraDirectory(body, records).map(d => d.key));
  let key;
  do { key = extraKey(`EXTRA:${newUid()}`); } while (keys.has(key));
  const selectedKey = scene.getAttribute('data-scene-uid') || newUid();
  scene.setAttribute('data-scene-uid', selectedKey);
  const number = Number(scene.getAttribute('data-scene-index')) || sceneIndex + 1;
  assignExtraScenes(body, key, [selectedKey], { key, name: entered, quantity: 1, description: '', originSceneUid: selectedKey, originSceneNumber: number });
  return key;
};
